import { useMemo } from 'react';

import type { Ati } from '@atlassian/ari';

import { useUniversalCreateState } from '../context';
import { extractNativeAtis } from '../utils';

/**
 * Hook that determines the native ATIs to pass to the useUCSuggestions hook.
 *
 * This hook automatically accesses the Universal Create state and handles the logic for
 * extracting or processing native ATIs based on:
 * - Suggestion configuration state
 * - Native ATIs configuration (auto extraction vs manual definition)
 *
 * @returns An array of ATI strings to pass to useUCSuggestions, or undefined if not applicable
 *
 * @example
 * ```tsx
 * const nativeAtis = useSuggestionNativeAtis();
 * const { data } = useUCSuggestions({
 *   // ... other params
 *   nativeAtis,
 * });
 * ```
 */
export function useSuggestionNativeAtis(): string[] | undefined {
	// Access the Universal Create state
	const { suggestionConfig, items } = useUniversalCreateState();

	// Determine native ATIs to pass to the hook
	const nativeAtis = useMemo(() => {
		if (!suggestionConfig.enabled || !suggestionConfig.nativeAtis) {
			return undefined;
		}

		// If nativeAtis is 'auto', extract ATIs from native menu items
		if (suggestionConfig.nativeAtis === 'auto') {
			return extractNativeAtis(items);
		}

		// If nativeAtis is manually defined as Ati[], convert to strings and use it
		if (Array.isArray(suggestionConfig.nativeAtis) && suggestionConfig.nativeAtis.length > 0) {
			return suggestionConfig.nativeAtis.map((ati: Ati) => ati.toString());
		}

		// Default fallback
		return undefined;
	}, [suggestionConfig, items]);

	return nativeAtis;
}
